import { useState, useEffect, useCallback, useMemo } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { EntityFormDialog } from '@/shared/components';
import { Separator } from '@/components/ui/separator';
import { ModalErrorBoundary } from '@/components/ErrorBoundary';
import { useHasRole } from '@/hooks/useSession';
import { useIsSuperAdmin } from '@/hooks/useIsSuperAdmin';
import { useSensorEdit, useUpdateSensor } from '../hooks/useSensorEdit';
import { SensorIdentityFields } from './SensorIdentityFields';
import { SensorAssignmentFields } from './SensorAssignmentFields';
import { SensorInfoCard } from './SensorInfoCard';
import { SensorStatusFlags } from './SensorStatusFlags';
import { SensorEquipmentFields } from './SensorEquipmentFields';
import { SensorTagsDescription } from './SensorTagsDescription';
import { SensorInvoicesSection } from './SensorInvoicesSection';
import { SensorGroupsField } from './SensorGroupsField';
import { SensorDailyLogFields } from './SensorDailyLogFields';
import type { SensorEditData, SensorEditForm, DailyLogForm } from '../types';

// =============================================================================
// Form Helpers
// =============================================================================

function initFormData(sensor: SensorEditData): SensorEditForm {
  return {
    name: sensor.name || '',
    customid: sensor.customid || '',
    input_number: String(sensor.input_number ?? ''),
    sensortype: sensor.sensortype || '',
    usergroup: sensor.usergroup || '',
    site: sensor.site || '',
    location: sensor.location || '',
    stream: sensor.stream || '',
    billable: sensor.billable ?? false,
    maintenance: sensor.maintenance ?? false,
    archived: sensor.archived ?? false,
    calibrationIncluded: sensor.calibrationIncluded ?? false,
    onsiteCalibrationOverride: sensor.onsiteCalibrationOverride ?? 'inherit',
    manufacturer: sensor.manufacturer || '',
    model: sensor.model || '',
    serialnumber: sensor.serialnumber || '',
    tags: sensor.tags || [],
    description: sensor.description || '',
    invoices: sensor.invoices || [],
    sensorgroups: sensor.sensorgroups || [],
    dailylog: {
      am: sensor.dailylog?.am != null ? String(sensor.dailylog.am) : '',
      pm: sensor.dailylog?.pm != null ? String(sensor.dailylog.pm) : '',
      threshold_min: sensor.dailylog?.threshold_min != null ? String(sensor.dailylog.threshold_min) : '',
      threshold_max: sensor.dailylog?.threshold_max != null ? String(sensor.dailylog.threshold_max) : '',
    },
  };
}

function diffSensorForm(
  original: SensorEditForm,
  current: SensorEditForm,
): Partial<SensorEditForm> {
  const changes: Partial<SensorEditForm> = {};
  for (const key of Object.keys(current) as (keyof SensorEditForm)[]) {
    if (JSON.stringify(original[key]) !== JSON.stringify(current[key])) {
      (changes as Record<string, unknown>)[key] = current[key];
    }
  }
  return changes;
}

// =============================================================================
// Component
// =============================================================================

interface EditSensorDialogProps {
  sensorId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditSensorDialog({
  sensorId,
  open,
  onOpenChange,
}: EditSensorDialogProps) {
  const isAdmin = useHasRole('full_admin');
  const isSuperAdmin = useIsSuperAdmin();

  // Data
  const editQuery = useSensorEdit(sensorId, open);
  const updateMutation = useUpdateSensor();

  // Form state
  const [form, setForm] = useState<SensorEditForm | null>(null);
  const [original, setOriginal] = useState<SensorEditForm | null>(null);

  // Initialize form when data loads (only when form is null to prevent overwrite)
  useEffect(() => {
    if (editQuery.data?.sensor && !form) {
      const init = initFormData(editQuery.data.sensor);
      setForm(init);
      setOriginal(init);
    }
  }, [editQuery.data?.sensor, form]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setForm(null);
      setOriginal(null);
    }
  }, [open]);

  const isDirty = useMemo(() => {
    if (!form || !original) return false;
    return JSON.stringify(form) !== JSON.stringify(original);
  }, [form, original]);

  const isValid = useMemo(() => {
    if (!form) return false;
    if (!form.name.trim()) return false;
    if (!isAdmin && !form.usergroup) return false;
    return true;
  }, [form, isAdmin]);

  const handleFieldChange = useCallback(
    (field: string, value: string | boolean | string[] | DailyLogForm) => {
      // (covers the #1118 on-site exception, which is a 3-value string)
      setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
    },
    [],
  );

  const handleSave = async () => {
    if (!form || !original || !sensorId) return;
    const changes = diffSensorForm(original, form);
    if (Object.keys(changes).length === 0) return;

    await updateMutation.mutateAsync({ sensorId, changes });
    onOpenChange(false);
  };

  const sensor = editQuery.data?.sensor;
  const options = editQuery.data?.options;
  const isLoading = editQuery.isLoading;

  return (
    <EntityFormDialog
      open={open}
      onOpenChange={onOpenChange}
      size="3xl"
      title={`Edit: ${sensor?.name || 'Sensor'}`}
      isDirty={isDirty}
      onSubmit={handleSave}
      isSubmitting={updateMutation.isPending}
      saveDisabled={!isDirty || !isValid}
      saveLabel={updateMutation.isPending ? 'Saving...' : 'Save'}
      confirmDiscardOnClose
    >
      <ModalErrorBoundary>
          {isLoading ? (
            <div className="space-y-4 py-4">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-1/2" />
            </div>
          ) : form && options ? (
            <div className="grid grid-cols-2 gap-6 py-2">
              {/* Left Column: Editable Fields */}
              <div className="space-y-6">
                <SensorIdentityFields
                  name={form.name}
                  customid={form.customid}
                  input_number={form.input_number}
                  onChange={handleFieldChange}
                />
                <SensorAssignmentFields
                  sensortype={form.sensortype}
                  usergroup={form.usergroup}
                  site={form.site}
                  location={form.location}
                  options={options}
                  isAdmin={isAdmin}
                  onChange={handleFieldChange}
                />
                {options.sensorgroups && options.sensorgroups.length > 0 && (
                  <SensorGroupsField
                    selected={form.sensorgroups}
                    options={options.sensorgroups}
                    onChange={handleFieldChange}
                  />
                )}
              </div>

              {/* Right Column: Info + Flags + Collapsibles */}
              <div className="space-y-4">
                {isAdmin && sensor && (
                  <SensorInfoCard
                    sensor={sensor}
                    options={options}
                    stream={form.stream}
                    isAdmin={isAdmin}
                    onChange={handleFieldChange}
                  />
                )}

                <SensorStatusFlags
                  billable={form.billable}
                  maintenance={form.maintenance}
                  archived={form.archived}
                  calibrationIncluded={form.calibrationIncluded}
                  calibrationIsInherited={sensor?.calibrationIsInherited}
                  onsiteCalibrationOverride={form.onsiteCalibrationOverride}
                  isSuperAdmin={isSuperAdmin}
                  onChange={handleFieldChange}
                />

                <Separator />

                <SensorDailyLogFields
                  dailylog={form.dailylog}
                  onChange={handleFieldChange}
                />

                <SensorEquipmentFields
                  manufacturer={form.manufacturer}
                  model={form.model}
                  serialnumber={form.serialnumber}
                  onChange={handleFieldChange}
                />

                <SensorTagsDescription
                  tags={form.tags}
                  description={form.description}
                  onChange={handleFieldChange}
                />

                {isSuperAdmin && (
                  <SensorInvoicesSection
                    invoices={form.invoices}
                    onChange={(invoices) => handleFieldChange('invoices', invoices)}
                  />
                )}
              </div>
            </div>
          ) : editQuery.error ? (
            <div className="py-8 text-center">
              <p className="text-destructive">Failed to load sensor data.</p>
            </div>
          ) : null}
      </ModalErrorBoundary>
    </EntityFormDialog>
  );
}
